import ServerSideService from '../server-side-service/server-side-service';
import AppConfigurationService from '../app-configuration-service/app-configuration-service';
import {
    CSP_DOMAINS,
    DSL_API_PATH,
    DSL_BASE_URL,
    FP_APP_IDS,
} from '@constants';
import HttpService from '../http-service/http-service';
import { SwapTokenResponse } from '@models/profile-with-vehicles';
import { Buffer } from 'buffer';
import { base64Url } from '@services/authentication-service/utils/base-64-util';
import { getFmaState } from '@services/authentication-service/utils/fma-state';
import { isWebviewFromSearch } from '@services/authentication-service/utils/is-webview';
import { PasskeyActions } from '@views/sign-in-credentials-view/passkeys/passkeys-section';
import { CustomMarketParams } from '@/views/sign-in-credentials-view/sign-in-credentials-view';

export default class AuthenticationService {
    private readonly AUTHENTICATED_EVENT = 'fma_authenticated';
    private readonly UNAUTHENTICATED_EVENT = 'fma_unauthenticated';
    private win: any;

    private handleAuthenticatedEvent: (() => void) | null = null;
    private handleUnauthenticatedEvent: (() => void) | null = null;
    private isAuthenticatedPromise: Promise<boolean>;

    constructor() {
        if (ServerSideService.isClientSide()) {
            this.win = window;
        }

        this.isAuthenticatedPromise = new Promise<boolean>((resolve) => {
            const isFmaAuthenticated = this.fmaAlreadyAuthenticated();
            if (this.hasWebviewToken() || isFmaAuthenticated) {
                resolve(true);
            } else if (isFmaAuthenticated === false) {
                resolve(false);
            } else if (ServerSideService.isClientSide()) {
                this.addAuthenticationEventListeners(resolve);
            }
        });
    }

    private fmaAlreadyAuthenticated = (): boolean | undefined => {
        return this.win?.fma?.isAuthenticated;
    };

    private readonly hasWebviewToken = (): boolean => {
        const webviewAccessToken =
            this.win?.SERVER_CONTEXT?.webviewData?.accessToken ||
            this.win?.webviewBundle?.accessToken;
        return Boolean(webviewAccessToken);
    };

    public getCatBundle = (): any | null => {
        const webviewData =
            this.win !== undefined && typeof window !== 'undefined'
                ? (window as any).SERVER_CONTEXT?.webviewData
                : {};
        if (webviewData?.accessToken) {
            return {
                access_token: webviewData.accessToken,
            };
        } else if (this.win?.webviewBundle) {
            return { access_token: this.win?.webviewBundle.accessToken };
        } else {
            return this.win?.fma?.CATBundle;
        }
    };

    public getAccessToken = (): string | null => {
        return this.win?.fma?.accessToken;
    };

    public getSwapToken = (appText: string): Promise<SwapTokenResponse> => {
        const authService = new AuthenticationService();

        const appConfigService: AppConfigurationService =
            new AppConfigurationService();

        const dslUrl = appConfigService.getAppConfiguration().dslUrl
            ? appConfigService.getAppConfiguration().dslUrl
            : DSL_BASE_URL;
        const url = dslUrl + DSL_API_PATH.TOKEN_SWAP;

        return HttpService.put<any>(
            url,
            {
                refresh_token: authService.getCatBundle()?.refresh_token,
                app_text: appText,
            },
            {
                headers: HttpService.getConsumerKeyAndAuthTokenRequestHeaders(),
            }
        ).then((response) => {
            return response.data;
        });
    };

    private addAuthenticationEventListeners = (
        resolve: (value?: boolean | PromiseLike<boolean> | undefined) => void
    ): void => {
        document.body.addEventListener(
            this.AUTHENTICATED_EVENT,
            (this.handleAuthenticatedEvent = () => {
                resolve(true);
                this.removeAuthenticationEventListeners();
            })
        );
        document.body.addEventListener(
            this.UNAUTHENTICATED_EVENT,
            (this.handleUnauthenticatedEvent = () => {
                resolve(false);
                this.removeAuthenticationEventListeners();
            })
        );
    };

    private removeAuthenticationEventListeners = (): void => {
        if (this.handleAuthenticatedEvent)
            document.body.removeEventListener(
                this.AUTHENTICATED_EVENT,
                this.handleAuthenticatedEvent
            );
        if (this.handleUnauthenticatedEvent)
            document.body.removeEventListener(
                this.UNAUTHENTICATED_EVENT,
                this.handleUnauthenticatedEvent
            );
    };

    public login = (): void => {
        sessionStorage.clear();
        this.win?.fma && this.win.fma.login();
    };

    public logout(): void {
        sessionStorage.clear();
        this.win?.fma && this.win.fma.logout();
    }

    public register = (): void => {
        sessionStorage.clear();
        this.win?.fma && this.win.fma.register();
    };

    public changeUsername = (): void => {
        sessionStorage.removeItem('postLoginNavComplete');
        this.win?.fma && this.win.fma.changeUsername();
    };

    public changePassword = (): void => {
        sessionStorage.removeItem('postLoginNavComplete');
        this.win?.fma && this.win.fma.changePassword();
    };

    public addMFAEmailAuthentication = (): void => {
        sessionStorage.removeItem('postLoginNavComplete');
        this.win?.fma && this.win.fma.addEmailMfa();
    };

    public changeMFAEmailAuthentication = (): void => {
        sessionStorage.removeItem('postLoginNavComplete');
        this.win?.fma && this.win.fma.editEmailMfa();
    };

    public addMFAAppAuthentication = (): void => {
        sessionStorage.removeItem('postLoginNavComplete');
        this.win?.fma && this.win.fma.addTotpMfa();
    };

    public changeMFAAppAuthentication = (): void => {
        sessionStorage.removeItem('postLoginNavComplete');
        this.win?.fma && this.win.fma.editTotpMfa();
    };

    public addMFASmsAuthentication = (): void => {
        sessionStorage.removeItem('postLoginNavComplete');
        this.win?.fma && this.win.fma.addSmsMfa();
    };

    public changeMFASmsAuthentication = (): void => {
        sessionStorage.removeItem('postLoginNavComplete');
        this.win?.fma && this.win.fma.editSmsMfa();
    };

    public editProfile = (): void => {
        sessionStorage.removeItem('postLoginNavComplete');
        this.win?.fma && this.win.fma.editProfile();
    };

    public onIsAuthenticated = (): Promise<boolean> => {
        return this.isAuthenticatedPromise;
    };

    public getState = (): string => {
        return this.win?.fma ? this.win.fma.state : '';
    };

    public getStateFromConfig = (): string => {
        return this.win?.fma ? this.win.fma.model.config.state : '';
    };

    public updateState = (path: string): void => {
        if (this.win?.fma) this.win.fma.model.config.state = path;
    };

    public updateLogoutURL = (url: string): void => {
        if (this.win?.fma) this.win.fma.model.config.logoutURL = url;
    };

    private getOrigin(): string {
        const appConfiguration = new AppConfigurationService();

        return `https://${window.location.hostname}${
            appConfiguration.brand &&
            appConfiguration.currentLanguageRegionCode &&
            appConfiguration.currentRoot ===
                `/${appConfiguration.brand}/${appConfiguration.currentLanguageRegionCode}/`
                ? '/'
                : appConfiguration.currentRoot
        }`;
    }

    public setFmaRedirectUrl(url: string): void {
        const urlWithAccountDashboardName =
            url?.split('//')[1]?.split('/')[1] === 'account-dashboard';

        // GA simplified brand roots use the bare origin as the registered
        // redirect URI — the path travels in `state` instead. For
        // Akamai/market roots (/myaccount/, etc.) the full origin+root is
        // registered, so keep it.
        const appCfg = new AppConfigurationService();
        const isBrandRoot =
            appCfg.currentRoot.startsWith('/ford/') ||
            appCfg.currentRoot.startsWith('/lincoln/');
        let origin: string;
        if (isBrandRoot) {
            origin = window.location.origin;
        } else if (urlWithAccountDashboardName) {
            origin = this.getOrigin() + 'account-dashboard';
        } else {
            origin = this.getOrigin();
        }
        const path = window.location.href.replace(origin, '');
        if (this.win?.fma) {
            this.updateState(url ? url.replace(origin, '') : path);
            this.win.fma.model.config.redirectUrl = origin;
            // set logout and origin URLs if missing(in case of syndicated header)
            !this.win.fma.model.config.logoutURL &&
                (this.win.fma.model.config.logoutURL = origin);
            !this.win.fma.model.config.originURL &&
                (this.win.fma.model.config.originURL = origin);
        }
    }

    public setFmaState() {
        if (this.win?.fma) {
            const { root } = this.win.SERVER_CONTEXT;

            // GA simplified brand roots use the bare origin as the registered
            // redirect URI; the path travels in `state`. For Akamai/market
            // roots the full origin+root is registered.
            const isBrandRoot =
                root.startsWith('/ford/') || root.startsWith('/lincoln/');
            this.win.fma.model.config.redirectUrl = isBrandRoot
                ? window.location.origin
                : window.location.origin + root;

            this.win.fma.model.config.state = getFmaState(
                window.location.pathname,
                window.location.search,
                root
            );
        }
    }

    public getFmaState(): string | undefined {
        if (this.win?.fma) {
            return this.win?.fma.state;
        }
    }

    updateStateWithDeepLink() {
        const currentState = this.getStateFromConfig();
        const stateContainsQueryParam = RegExp(/.\?\w*=\w*/).test(currentState);
        if (currentState && !currentState.includes('appLogout=')) {
            if (stateContainsQueryParam) {
                this.updateState(currentState + '&appLogout=true');
            } else {
                this.updateState(currentState + '?appLogout=true');
            }
        } else if (!currentState) {
            const path = window.location.href.replace(this.getOrigin(), '');
            this.updateState(path + '?appLogout=true');
        }
    }

    public async generatePasskeysUrlAndVerifier(
        passkeyAction: PasskeyActions | null = null
    ): Promise<{
        fmaPasskeysUrls: Record<string, URL>;
        verifier: string;
    }> {
        const urls: Record<string, URL> = {};
        let code_verifier = '';
        await this.constructAuthorizeUrl(passkeyAction, null).then(
            ({ verifier, defaultUrl }) => {
                urls.default = defaultUrl.default;
                code_verifier = verifier;
            }
        );

        urls.passkey = new URL(urls.default);
        urls.passkey.searchParams.set('change_flow', 'passkey');

        urls.passkeyAdd = new URL(urls.passkey);
        urls.passkeyAdd.searchParams.set('passkey_flow_action', 'add');

        urls.passkeyRemove = new URL(urls.passkey);
        urls.passkeyRemove.searchParams.set('passkey_flow_action', 'remove');

        return Promise.resolve({
            fmaPasskeysUrls: urls,
            verifier: code_verifier,
        });
    }

    public async customOTP(customMarketParams: CustomMarketParams): Promise<{
        otpUrls: Record<string, URL>;
        verifier: string;
    }> {
        const urls: Record<string, URL> = {};
        let code_verifier = '';
        await this.constructAuthorizeUrl(null, customMarketParams).then(
            ({ verifier, defaultUrl }) => {
                urls.default = defaultUrl.default;
                code_verifier = verifier;
            }
        );

        urls.username = new URL(urls.default);
        urls.username.searchParams.set('change_flow', 'username');
        urls.password = new URL(urls.default);
        urls.password.searchParams.set('change_flow', 'password');

        return Promise.resolve({
            otpUrls: urls,
            verifier: code_verifier,
        });
    }

    private async constructAuthorizeUrl(
        passkeyAction: PasskeyActions | null = null,
        customMarket: CustomMarketParams
    ): Promise<{
        defaultUrl: Record<string, URL>;
        verifier: string;
    }> {
        const appConfig = new AppConfigurationService().getAppConfiguration();
        const activeProfile: string =
            process.env.REACT_APP_ACTIVE_PROFILE || appConfig.environment;
        const isPreProd: boolean =
            !!activeProfile && activeProfile.toLowerCase() !== 'prod';

        const textToBytes = (s) => new TextEncoder().encode(s);

        const normaliseLocale = (raw: string) => {
            const [lang = '', region = ''] = raw.trim().split(/[-_]/);

            return {
                hyphen: `${lang.toLowerCase()}-${region.toUpperCase()}`,
                underscore: `${lang.toLowerCase()}_${region.toUpperCase()}`,
            };
        };

        const cfg = this.win?.fma?.model?.__config || {};

        const tenantId = isPreProd
            ? 'e75ff8bf-6627-43e3-b410-9fc74d252d29'
            : '4566605f-43a7-400a-946e-89cc9fdb0bd7';

        const { hyphen: langHyphen, underscore: langUnder } = normaliseLocale(
            customMarket?.uiLocales || appConfig.languageRegionCode || ''
        );

        const rootDomain = this.constructRoot(
            isPreProd,
            appConfig.languageRegionCode?.split('-')[1],
            appConfig.brand
        );
        const appendBrandToPolicyIfLincoln = (brand: string): string => {
            return brand === 'lincoln' ? 'lincoln_' : '';
        };

        const policy = `B2C_1A_${
            isPreProd ? 'qa_' : ''
        }ProfileEditCombined_${appendBrandToPolicyIfLincoln(
            appConfig.brand
        )}${langHyphen}`;

        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        const isWebview = isWebviewFromSearch(globalThis.location?.search);
        const isQa = window.location.hostname.includes('qa');
        const hasAddedRoot = activeProfile === 'prod' || isQa;
        let pathname = url.pathname;

        // For brand roots keep the root in the path so FMA can navigate back
        // from the bare-origin redirect URI. For Akamai/market roots strip it.
        const isBrandRoot =
            appConfig.root.startsWith('/ford/') ||
            appConfig.root.startsWith('/lincoln/');
        if (
            hasAddedRoot &&
            pathname.startsWith(appConfig.root) &&
            !isBrandRoot
        ) {
            pathname = pathname.slice(appConfig.root.length);
        }

        params.delete('passkeyAdd');
        params.delete('passkeyRemove');

        if (passkeyAction === PasskeyActions.ADD) {
            params.set('passkeyAdd', 'true');
        } else if (passkeyAction === PasskeyActions.REMOVE) {
            params.set('passkeyRemove', 'true');
        }

        const finalPathname = `${pathname}${
            params.toString() ? `?${params.toString()}` : ''
        }`;

        const encodedSignInPath = Buffer.from(finalPathname).toString('base64');

        const stateObj = {
            policy: 'edit_profile_policy',
            lang: langUnder?.toLowerCase(),
            state: encodedSignInPath,
            queryHash: '',
            existingPath: '',
            forwardUrl: '',
        };
        const encodedState = JSON.stringify(stateObj);

        const randomBytes = crypto.getRandomValues(new Uint8Array(32));
        const code_verifier = base64Url(randomBytes);

        const hashBuffer = await crypto.subtle.digest(
            'SHA-256',
            textToBytes(code_verifier)
        );
        const code_challenge = base64Url(new Uint8Array(hashBuffer));

        const getFPAppID = (): string => {
            if (activeProfile == 'prod') {
                return navigator.userAgent.toLowerCase().includes('android')
                    ? FP_APP_IDS.PROD.ANDROID
                    : FP_APP_IDS.PROD.IOS;
            } else {
                return navigator.userAgent.toLowerCase().includes('android')
                    ? FP_APP_IDS.STAGE.ANDROID
                    : FP_APP_IDS.STAGE.IOS;
            }
        };

        let fordApplicationId = appConfig.appId;

        if (customMarket) {
            fordApplicationId = getFPAppID();
        }

        if (isWebview && appConfig.mobileAppId) {
            fordApplicationId = appConfig.mobileAppId;
        }

        // Brand roots (/ford/…, /lincoln/…): use bare origin. The path travels
        // through state so B2C never receives an unregistered rooted redirect.
        // Akamai/market roots (/myaccount/, etc.): origin+root (web B2C client).
        // Non-webview: use cfg.redirectUrl (existing behaviour).
        let redirectUri: string;
        if (isBrandRoot) {
            redirectUri = `${globalThis.location.protocol}//${globalThis.location.host}`;
        } else if (isWebview) {
            redirectUri = `${globalThis.location.protocol}//${globalThis.location.host}${appConfig.root}`;
        } else {
            redirectUri = cfg.redirectUrl;
        }

        const buildBase = (): URL => {
            const u = new URL(
                `${rootDomain}/${tenantId}/oauth2/v2.0/authorize${
                    hasAddedRoot && passkeyAction ? '?tx=StateProperties' : ''
                }`
            );
            u.searchParams.set('p', policy);
            u.searchParams.set('client_id', appConfig.clientId);
            u.searchParams.set('redirect_uri', redirectUri);
            u.searchParams.set('response_type', 'code');
            u.searchParams.set(
                'scope',
                customMarket ? appConfig.clientId + ' openid' : 'openid'
            );
            u.searchParams.set('ui_locales', langHyphen);
            u.searchParams.set('template_id', 'Ford-MFA-Authentication');
            u.searchParams.set('ford_application_id', fordApplicationId);
            u.searchParams.set(
                'country_code',
                customMarket
                    ? customMarket.countryCode
                    : appConfig.threeLetterCountryCode.toUpperCase()
            );
            u.searchParams.set('language_code', langHyphen);
            u.searchParams.set('state', encodedState);
            u.searchParams.set('code_challenge', code_challenge);
            u.searchParams.set('code_challenge_method', 'S256');

            return u;
        };

        const urls: Record<string, URL> = {};

        urls.default = buildBase();

        return Promise.resolve({
            defaultUrl: urls,
            verifier: code_verifier,
        });
    }

    private constructRoot(
        isPreProd: boolean,
        region: string,
        brand: string
    ): string {
        const root = isPreProd ? 'https://loginqa' : 'https://login';

        let preRoot = `${root}.ford.com`;

        if (
            (process.env.REACT_APP_ACTIVE_PROFILE === 'perf' &&
                window.location.hostname.includes('perf')) ||
            process.env.REACT_APP_ACTIVE_PROFILE === 'local'
        ) {
            return `${root}.${brand}.com`;
        }

        if (brand === 'lincoln') {
            if (region === 'us') {
                preRoot = `${root}.lincoln.com`;
            }
            if (region === 'ca') {
                preRoot = `${root}.lincolncanada.com`;
            }
            if (region === 'mx') {
                preRoot = `${root}.lincoln.mx`;
            }
        } else if (region === 'gb') {
            preRoot = `${root}.ford.co.uk`;
        } else if (region === 'co') {
            preRoot = `${root}.ford.com.co`;
        } else if (region !== 'us' && region !== 'se') {
            const regionalDomain = CSP_DOMAINS.find((domain) => {
                if (domain.includes('.' + region)) {
                    return domain;
                }
            });
            if (regionalDomain) {
                preRoot = regionalDomain.replace('*', root);
            }
        }

        return preRoot;
    }
}
